<script lang="ts">
export default {
  name: 'PromptPlaygroundPage',
};

interface ModelOption {
  value: string;
  label: string;
  description: string;
}
</script>

<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import Card from '@/components/ui/card/Card.vue';
import Badge from '@/components/ui/Badge.vue';
import Separator from '@/components/ui/Separator.vue';
import UISelect from '@/components/ui/UISelect.vue';
import DocumentSection from '@/components/ui/DocumentSection.vue';
import PlaygroundPromptSection from '@/components/ui/PlaygroundPromptSection.vue';
import ExecutionSection from '@/components/ui/ExecutionSection.vue';
import ResultsSection from '@/components/ui/ResultsSection.vue';
import PromptHistoryModal from '@/components/ui/PromptHistoryModal.vue';
import type { Prompt, ExecutionResult } from '@/types/ui/PromptManager';
import type { Document } from '@/types/ui/PromptManager';
import { aiService } from '@/services/AIService';
import { X } from '@lucide/vue';
import {
  InvokeModelRequest,
  newConverseInput,
  ModelInvocationStatus,
} from '@/types/ai';
import { useModelInvocation } from '@/composables/useModelInvocation';

const selectedDocument = ref<Document | null>(null);
const selectedPrompt = ref<Prompt | null>(null);
const currentPromptContent = ref<string>('');
const currentSystemText = ref<string>('');
const currentExpectedOutput = ref<string>('');
const hasUnsavedChanges = ref(false);
const executionResult = ref<ExecutionResult | null>(null);
const isExecuting = ref(false);
const executionError = ref<string>('');
const selectedModel = ref<string>('');
const availableModels = ref<ModelOption[]>([]);
const historyModalRef = ref<InstanceType<typeof PromptHistoryModal> | null>(null);
const isTestMode = ref(false);
const currentInvocationId = ref<string | null>(null);
const invocationStatus = ref<ModelInvocationStatus | null>(null);

const { pollInvocationStatus } = useModelInvocation();
const route = useRoute();

async function applyPlaygroundQueryParams() {
  const q = route.query;
  const documentId = q.documentId as string | undefined;
  const accountId = q.accountId as string | undefined;
  const s3Key = q.s3Key as string | undefined;
  const documentName = q.documentName as string | undefined;
  const promptId = q.promptId as string | undefined;
  const promptVersion = q.promptVersion as string | undefined;
  const modelArn = q.modelArn as string | undefined;

  if (documentId && accountId) {
    const doc: Document = {
      documentId,
      accountId: parseInt(accountId, 10),
      name: documentName || '',
      displayName: documentName || '',
    };
    handleDocumentSelect(doc);
  } else if (s3Key) {
    const doc: Document = {
      documentId: s3Key,
      s3Key,
      name: documentName || 'Document',
      displayName: documentName,
    };
    handleDocumentSelect(doc);
  }

  if (promptId && promptVersion) {
    const prompts = await aiService.getAllPrompts();
    const prompt = prompts.find((p) => String(p.promptId) === promptId && p.version === promptVersion) as Prompt | undefined;
    if (prompt) {
      handlePromptSelect(prompt);
    }
  }

  if (modelArn && availableModels.value.some((m) => m.value === modelArn)) {
    selectedModel.value = modelArn;
  }
}

onMounted(async () => {
  availableModels.value = (await aiService.getAllModels()).map((model) => ({
    value: model.arn,
    label: model.name,
    description: model.description || 'No description available',
  }));

  await nextTick();
  await tryApplyPlaygroundQueryParams();
});

watch(
  () => route.query,
  () => tryApplyPlaygroundQueryParams(),
  { deep: true },
);

async function tryApplyPlaygroundQueryParams() {
  if (route.query.tab === 'playground' && (route.query.documentId || route.query.s3Key || route.query.promptId || route.query.modelArn)) {
    await applyPlaygroundQueryParams();
  }
}

const canExecute = computed(() => (selectedModel.value && currentPromptContent.value && !isExecuting.value) || false);

const handleDocumentSelect = (document: Document | null) => {
  selectedDocument.value = document;
};

const handlePromptSelect = (prompt: Prompt) => {
  if (prompt.currentPromptText === undefined) {
    prompt.currentPromptText = prompt.userPrompt;
  }
  if (prompt.currentSystemText === undefined) {
    prompt.currentSystemText = prompt.systemPrompt;
  }
  if (prompt.currentExpectedOutput === undefined) {
    prompt.currentExpectedOutput = prompt.expectedOutputSchema;
  }

  selectedPrompt.value = prompt;
  currentPromptContent.value = prompt.currentPromptText || prompt.userPrompt || '';
  currentSystemText.value = prompt.currentSystemText || prompt.systemPrompt || '';
  currentExpectedOutput.value = prompt.currentExpectedOutput || prompt.expectedOutputSchema || '';
  isTestMode.value = false;
  hasUnsavedChanges.value = currentPromptContent.value !== (prompt.userPrompt || '') || currentSystemText.value !== (prompt.systemPrompt || '');
};

const handlePromptContentChange = (content: string) => {
  currentPromptContent.value = content;
  if (selectedPrompt.value) {
    selectedPrompt.value.currentPromptText = content;
    hasUnsavedChanges.value =
      content !== (selectedPrompt.value.userPrompt || '') ||
      (selectedPrompt.value.currentSystemText || '') !== (selectedPrompt.value.systemPrompt || '');
  }
};

const handleSystemInstructionsChange = (content: string) => {
  currentSystemText.value = content;
  if (selectedPrompt.value && !isTestMode.value) {
    selectedPrompt.value.currentSystemText = content;
    hasUnsavedChanges.value =
      currentPromptContent.value !== (selectedPrompt.value.userPrompt || '') || content !== (selectedPrompt.value.systemPrompt || '');
  }
};

const handleTestPromptChange = (systemText: string, promptText: string) => {
  currentSystemText.value = systemText;
  currentPromptContent.value = promptText;
  currentExpectedOutput.value = ''; // Clear expected output for test prompts
  isTestMode.value = true;
  selectedPrompt.value = null;
  hasUnsavedChanges.value = false;
};

function handlePromptReset(prompt: Prompt) {
  currentPromptContent.value = prompt.userPrompt || '';
  currentExpectedOutput.value = prompt.expectedOutputSchema || '';
  if (selectedPrompt.value) {
    selectedPrompt.value.currentPromptText = prompt.userPrompt || '';
    selectedPrompt.value.currentSystemText = prompt.systemPrompt || '';
    selectedPrompt.value.currentExpectedOutput = prompt.expectedOutputSchema || '';
  }
  hasUnsavedChanges.value = false;
}

const handleExecute = async () => {
  if (!selectedDocument.value || !currentPromptContent.value) return;

  // Clear previous error and result
  executionError.value = '';
  executionResult.value = null;
  currentInvocationId.value = null;
  invocationStatus.value = null;

  try {
    isExecuting.value = true;
    invocationStatus.value = 'PENDING';

    const invokeRequest: InvokeModelRequest = {
      modelId: selectedModel.value,
      converseInput: newConverseInput(currentPromptContent.value, currentSystemText.value),
      documentId: selectedDocument.value.documentId,
      s3Key: selectedDocument.value.s3Key,
      expectedOutputSchema: currentExpectedOutput.value,
      promptId: selectedPrompt.value ? Number(selectedPrompt.value.promptId) : undefined,
      promptVersion: selectedPrompt.value?.version,
    };

    // Invoke model asynchronously
    // For playground documents, use s3Key; for platform documents, use documentId
    const asyncResponse = await aiService.invokeModelAsync(invokeRequest);

    currentInvocationId.value = asyncResponse.invocationId;
    invocationStatus.value = 'PENDING';

    // Start polling for status
    pollInvocationStatus(
      asyncResponse.invocationId,
      async (statusResponse) => {
        if (statusResponse.status === 'COMPLETED') {
          // Get the result
          try {
            const result = await aiService.getInvocationResult(asyncResponse.invocationId);
            executionResult.value = {
              output: result.rawOutput,
            };
            invocationStatus.value = 'COMPLETED';
            isExecuting.value = false;
          } catch (error: any) {
            executionError.value = error.message || 'Failed to retrieve result';
            invocationStatus.value = 'FAILED';
            isExecuting.value = false;
          }
        } else if (statusResponse.status === 'FAILED') {
          executionError.value = statusResponse.errorMessage || 'Model invocation failed';
          invocationStatus.value = 'FAILED';
          isExecuting.value = false;
        }
      },
      (error) => {
        executionError.value = error.message || 'Failed to poll invocation status';
        invocationStatus.value = 'FAILED';
        isExecuting.value = false;
      },
      (statusResponse) => {
        // Update status as it changes
        invocationStatus.value = statusResponse.status;
      },
    );
  } catch (error: any) {
    isExecuting.value = false;
    invocationStatus.value = 'FAILED';
    executionError.value = error.message || 'An error occurred while executing the prompt. Please try again.';
    console.error('Execution error:', error);
  }
};

function formatVersion(version: string) {
  if (version.toLowerCase() === 'draft') {
    return 'DRAFT';
  } else {
    return `v${version}`;
  }
}

const handleShowHistory = () => {
  historyModalRef.value?.show();
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <!-- Header -->
    <h1 class="pl-2 text-2xl">Playground</h1>

    <!-- Status Bar -->
    <Card class="border-slate-200 bg-white/80 p-4 backdrop-blur-sm">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-4">
          <div class="flex items-center gap-2">
            <span class="text-md font-bold text-slate-600">Document Process:</span>
          </div>
          <Separator
            orientation="vertical"
            class="h-6" />
          <div class="flex items-center gap-2">
            <span class="text-sm font-medium text-slate-600">Document:</span>
            <div
              v-if="selectedDocument"
              class="relative inline-flex">
              <Badge
                variant="secondary"
                class="pr-2">
                <span class="max-w-[200px] truncate">{{ selectedDocument.displayName || selectedDocument.name }}</span>
              </Badge>
              <button
                @click="selectedDocument = null"
                class="absolute -top-2 -right-2 flex h-4 w-4 items-center justify-center rounded-full bg-base-200 text-red-500 opacity-80 transition-all hover:text-red-600 hover:opacity-100"
                title="Unselect document">
                <X class="h-4 w-4 transition-all hover:font-bold" />
              </button>
            </div>
            <Badge
              v-else
              variant="outline"
              >None selected</Badge
            >
          </div>
          <Separator
            orientation="vertical"
            class="h-6" />
          <div class="flex items-center gap-2">
            <span class="text-sm font-medium text-slate-600">Prompt:</span>
            <div
              v-if="isTestMode"
              class="flex items-center gap-2">
              <div class="relative inline-flex">
                <Badge
                  variant="secondary"
                  class="bg-purple-100 pr-2 text-purple-800">
                  Test Prompt
                </Badge>
                <button
                  @click="
                    isTestMode = false;
                    currentPromptContent = '';
                    currentSystemText = '';
                  "
                  class="absolute -top-2 -right-2 flex h-4 w-4 items-center justify-center rounded-full bg-base-200 text-red-500 opacity-80 transition-all hover:text-red-600 hover:opacity-100"
                  title="Clear test prompt">
                  <X class="h-4 w-4 transition-all hover:font-bold" />
                </button>
              </div>
            </div>
            <div
              v-else-if="selectedPrompt"
              class="flex items-center gap-2">
              <div class="relative inline-flex">
                <Badge
                  variant="secondary"
                  class="pr-2">
                  {{ selectedPrompt.name }} ({{ formatVersion(selectedPrompt.version) }})
                  <span
                    v-if="currentExpectedOutput"
                    class="ml-2 rounded bg-green-100 px-1 py-0.5 text-xs text-green-700"
                    title="This prompt has an expected output schema">
                    📋 Schema
                  </span>
                </Badge>
                <button
                  @click="
                    selectedPrompt = null;
                    currentPromptContent = '';
                    currentSystemText = '';
                    currentExpectedOutput = '';
                    hasUnsavedChanges = false;
                  "
                  class="absolute -top-2 -right-2 flex h-4 w-4 items-center justify-center rounded-full bg-base-200 text-red-500 opacity-80 transition-all hover:text-red-600 hover:opacity-100"
                  title="Unselect prompt">
                  <X class="h-4 w-4 transition-all hover:font-bold" />
                </button>
              </div>
              <span
                v-if="hasUnsavedChanges"
                class="relative flex h-2 w-2"
                title="Unsaved changes">
                <span class="absolute inline-flex h-full w-full animate-pulse rounded-full bg-amber-400 opacity-30"></span>
                <span class="relative inline-flex h-2 w-2 rounded-full bg-amber-500"></span>
              </span>
            </div>
            <Badge
              v-else
              variant="outline"
              >None selected</Badge
            >
          </div>
          <Separator
            orientation="vertical"
            class="h-6" />
          <div class="flex items-center gap-2">
            <span class="text-sm font-medium text-slate-600">Model:</span>
            <div
              v-if="selectedModel"
              class="relative inline-flex">
              <Badge
                variant="secondary"
                class="pr-2">
                {{ availableModels.find((m) => m.value === selectedModel)?.label || 'Unknown' }}
              </Badge>
              <button
                @click="selectedModel = ''"
                class="absolute -top-2 -right-2 flex h-4 w-4 items-center justify-center rounded-full bg-base-200 text-red-500 opacity-80 transition-all hover:text-red-600 hover:opacity-100"
                title="Unselect model">
                <X class="h-4 w-4 transition-all hover:font-bold" />
              </button>
            </div>
            <Badge
              v-else
              variant="outline"
              >None selected</Badge
            >
          </div>
        </div>
      </div>
    </Card>

    <!-- Model Configuration Section -->
    <Card class="border-slate-200 bg-white/80 p-6 backdrop-blur-sm">
      <div class="space-y-4">
        <div>
          <h2 class="mb-2 text-lg font-semibold">Model Configuration</h2>
          <p class="text-sm text-slate-600">Select the AWS Bedrock model to use for document processing</p>
        </div>
        <div class="max-w-md">
          <UISelect
            v-model="selectedModel"
            :options="availableModels"
            placeholder="Select a model" />
        </div>
      </div>
    </Card>

    <!-- Main Content Grid -->
    <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <!-- Left Column -->
      <div class="space-y-6">
        <DocumentSection
          :selectedDocument="selectedDocument"
          @documentSelect="handleDocumentSelect" />
        <ExecutionSection
          :canExecute="canExecute"
          :isExecuting="isExecuting"
          @execute="handleExecute"
          :selectedDocument="selectedDocument"
          :selectedModel="selectedModel"
          :promptContent="currentPromptContent"
          :error="executionError"
          :invocationId="currentInvocationId"
          :invocationStatus="invocationStatus" />
      </div>

      <!-- Right Column -->
      <div class="space-y-6">
        <PlaygroundPromptSection
          :selectedPrompt="selectedPrompt"
          :currentContent="currentPromptContent"
          :hasUnsavedChanges="hasUnsavedChanges"
          :expectedOutputSchema="currentExpectedOutput"
          @promptSelect="handlePromptSelect"
          @contentChange="handlePromptContentChange"
          @promptReset="handlePromptReset"
          @systemInstructionsChange="handleSystemInstructionsChange"
          @testPromptChange="handleTestPromptChange" />
        <ResultsSection
          :result="executionResult"
          @clear="executionResult = null"
          @show-history="handleShowHistory" />
      </div>
    </div>

    <!-- Prompt History Modal -->
    <PromptHistoryModal ref="historyModalRef" />
  </div>
</template>
